import React, { createContext, use, useState, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react';
import type {
  OrderType,
  OrderTypeData,
  TypedOrder,
  OrderDocument,
  CompletenessResult,
} from '@lds-efsp/shared-contracts';
import { calculateOrderCompleteness } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';

export interface PricingEstimateData {
  serviceFee: number;
  zone: string;
  serviceLevel: string;
}

export interface OrderFormState {
  orderId: string | null;
  orderType: OrderType;
  // Common fields
  caseNumber: string;
  caseTitle: string;
  courtLocationId: string;
  priority: string;
  notes: string;
  internalReferenceNumber: string;
  // Type-specific data
  typeData: Partial<OrderTypeData>;
  // Documents
  documents: OrderDocument[];
  // Pricing
  estimatedPrice: PricingEstimateData | null;
  isPricingLoading: boolean;
  // UI state
  isSubmitting: boolean;
  submitError: string | null;
  isDirty: boolean;
}

interface OrderFormContextValue extends OrderFormState {
  updateCommonFields: (updates: Partial<Pick<OrderFormState, 'caseNumber' | 'caseTitle' | 'courtLocationId' | 'priority' | 'notes' | 'internalReferenceNumber'>>) => void;
  updateTypeData: (updates: Partial<OrderTypeData>) => void;
  createDraft: () => Promise<string>;
  saveDraft: () => Promise<void>;
  submitOrder: () => Promise<TypedOrder>;
  uploadDocument: (file: File, category?: string) => Promise<OrderDocument>;
  removeDocument: (docId: string) => void;
  fetchPricing: (county: string, priority: string) => void;
  completeness: CompletenessResult;
}

const OrderFormContext = createContext<OrderFormContextValue | null>(null);

interface OrderFormProviderProps {
  orderType: OrderType;
  children: ReactNode;
  existingOrder?: TypedOrder;
  /** Account ID for platform admins acting on behalf of a company */
  accountId?: string | null;
}

export const OrderFormProvider: React.FC<OrderFormProviderProps> = ({
  orderType,
  children,
  existingOrder,
  accountId,
}) => {
  const { get, post, patch, fetchWithAuth, isAuthenticated } = useApi();

  // Build account header for platform admins
  const accountHeaders = accountId ? { 'X-Account-Id': accountId } : undefined;
  const savePendingRef = useRef(false);

  const pricingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<OrderFormState>(() => ({
    orderId: existingOrder?.id ?? null,
    orderType,
    caseNumber: existingOrder?.caseNumber ?? '',
    caseTitle: existingOrder?.caseTitle ?? '',
    courtLocationId: existingOrder?.courtLocation ?? '',
    priority: existingOrder?.priority ?? 'standard',
    notes: existingOrder?.notes ?? '',
    internalReferenceNumber: existingOrder?.internalReferenceNumber ?? '',
    typeData: existingOrder?.typeData ?? { orderType } as Partial<OrderTypeData>,
    documents: existingOrder?.documents ?? [],
    estimatedPrice: null,
    isPricingLoading: false,
    isSubmitting: false,
    submitError: null,
    isDirty: false,
  }));

  const updateCommonFields = useCallback((updates: Partial<Pick<OrderFormState, 'caseNumber' | 'caseTitle' | 'courtLocationId' | 'priority' | 'notes' | 'internalReferenceNumber'>>) => {
    setState(prev => ({ ...prev, ...updates, isDirty: true }));
  }, []);

  const updateTypeData = useCallback((updates: Partial<OrderTypeData>) => {
    setState(prev => ({
      ...prev,
      typeData: { ...prev.typeData, ...updates } as Partial<OrderTypeData>,
      isDirty: true,
    }));
  }, []);

  const buildPayload = useCallback(() => ({
    type: state.orderType,
    caseNumber: state.caseNumber || undefined,
    caseTitle: state.caseTitle || undefined,
    priority: state.priority || undefined,
    notes: state.notes || undefined,
    internalReferenceNumber: state.internalReferenceNumber || undefined,
    typeData: { orderType: state.orderType, ...state.typeData },
  }), [state]);

  const createDraft = useCallback(async (): Promise<string> => {
    const result = await post<TypedOrder>(`${API_BASE_URL}/api/orders`, buildPayload(), { headers: accountHeaders });
    setState(prev => ({ ...prev, orderId: result.id, isDirty: false }));
    return result.id;
  }, [post, buildPayload, accountHeaders]);

  const saveDraft = useCallback(async (): Promise<void> => {
    if (!state.orderId) return;
    await patch(`${API_BASE_URL}/api/orders/${state.orderId}`, buildPayload(), { headers: accountHeaders });
    setState(prev => ({ ...prev, isDirty: false }));
  }, [patch, state.orderId, buildPayload, accountHeaders]);

  const submitOrder = useCallback(async (): Promise<TypedOrder> => {
    setState(prev => ({ ...prev, isSubmitting: true, submitError: null }));
    try {
      let id = state.orderId;
      if (!id) {
        id = await createDraft();
      } else {
        await saveDraft();
      }
      const result = await post<TypedOrder>(`${API_BASE_URL}/api/orders/${id}/submit`, undefined, { headers: accountHeaders });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      setState(prev => ({ ...prev, submitError: message }));
      throw err;
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [state.orderId, createDraft, saveDraft, post, accountHeaders]);

  const uploadDocument = useCallback(async (file: File, category?: string): Promise<OrderDocument> => {
    let id = state.orderId;
    if (!id) {
      id = await createDraft();
    }
    const formData = new FormData();
    formData.append('file', file);
    if (category) formData.append('category', category);

    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}/documents`, {
      method: 'POST',
      body: formData,
      headers: accountHeaders ?? {},
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.message || error.error || 'Upload failed');
    }
    const doc: OrderDocument = await response.json();
    setState(prev => ({ ...prev, documents: [...prev.documents, doc] }));
    return doc;
  }, [state.orderId, createDraft, fetchWithAuth, accountHeaders]);

  const removeDocument = useCallback((docId: string) => {
    setState(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== docId),
      isDirty: true,
    }));
  }, []);

  const fetchPricing = useCallback((county: string, priority: string) => {
    // Cancel any pending pricing request
    if (pricingTimerRef.current) {
      clearTimeout(pricingTimerRef.current);
    }

    if (!county) {
      setState(prev => ({ ...prev, estimatedPrice: null, isPricingLoading: false }));
      return;
    }

    setState(prev => ({ ...prev, isPricingLoading: true }));

    pricingTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          orderType,
          county,
          priority: priority || 'standard',
        });
        const result = await get<PricingEstimateData>(
          `${API_BASE_URL}/api/orders/pricing/quote?${params}`,
        );
        setState(prev => ({ ...prev, estimatedPrice: result, isPricingLoading: false }));
      } catch {
        setState(prev => ({ ...prev, isPricingLoading: false }));
      }
    }, 500);
  }, [orderType, get]);

  // Auto-save to server when dirty (debounced)
  useEffect(() => {
    if (!state.isDirty || !state.orderId || !isAuthenticated) return;
    const timer = setTimeout(() => {
      if (!savePendingRef.current) {
        savePendingRef.current = true;
        saveDraft().finally(() => {
          savePendingRef.current = false;
        });
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [state.isDirty, state.orderId, isAuthenticated, saveDraft]);

  const completeness = useMemo(
    () => calculateOrderCompleteness({
      orderType: state.orderType,
      caseNumber: state.caseNumber,
      caseTitle: state.caseTitle,
      priority: state.priority,
      documents: state.documents,
      typeData: (state.typeData ?? {}) as Record<string, unknown>,
    }),
    [state.orderType, state.caseNumber, state.caseTitle, state.priority, state.documents, state.typeData],
  );

  const value: OrderFormContextValue = {
    ...state,
    updateCommonFields,
    updateTypeData,
    createDraft,
    saveDraft,
    submitOrder,
    uploadDocument,
    removeDocument,
    fetchPricing,
    completeness,
  };

  return (
    <OrderFormContext value={value}>
      {children}
    </OrderFormContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOrderForm = (): OrderFormContextValue => {
  const context = use(OrderFormContext);
  if (!context) {
    throw new Error('useOrderForm must be used within an OrderFormProvider');
  }
  return context;
};
